import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { isSuperAdmin } from 'utils/isSuperAdmin';
import { hasActiveProductPlanForModule } from 'utils/productPlan';

export type ModuleRole = 'user' | 'admin';

export interface ModulePermission {
  module: string;
  role: ModuleRole;
}

export const useModulePermissions = () => {
  const user = useSelector((state: RootState) => state.authentication.user);
  const company = useSelector((state: RootState) => state.authentication.company);
  
  // Check if user is SUPER-ADMIN based on email domain
  const isUserSuperAdmin = user?.email ? isSuperAdmin(user.email) : false;

  const hasModuleAccess = (module: string, requiredRole?: ModuleRole): boolean => {
    if (!user?.moduleAndRole) return false;
    
    const moduleAccess = user.moduleAndRole.find((mr: any) => mr.module === module);
    if (!moduleAccess) return false;
    
    if (requiredRole && moduleAccess.role !== requiredRole) return false;
    
    return true;
  };

  const hasInstantUpdateAccess = (requiredRole?: ModuleRole): boolean => {
    return hasModuleAccess('instantupdate.ai', requiredRole);
  };

  const hasAutomateTestAccess = (requiredRole?: ModuleRole): boolean => {
    return hasModuleAccess('automatetest.ai', requiredRole);
  };

  const hasAssetManagementAccess = (requiredRole?: ModuleRole): boolean => {
    return hasModuleAccess('assetsmanagement.ai', requiredRole);
  };

  /** User module access + company license includes assetsmanagement.ai (matches sidebar gating). */
  const canUseAssetManagement = (): boolean => {
    const licensed = hasActiveProductPlanForModule(company, 'assetsmanagement.ai');
    if (!licensed) return false;
    return hasAssetManagementAccess() || isUserSuperAdmin;
  };

  const hasInfralytiqsAccess = (requiredRole?: ModuleRole): boolean => {
    return hasModuleAccess('infralytiqs.com', requiredRole);
  };

  const hasAnamnesisCloudAccess = (requiredRole?: ModuleRole): boolean => {
    return hasModuleAccess('anamnesis.cloud', requiredRole);
  };

  const hasBeamonScreenAccess = (requiredRole?: ModuleRole): boolean => {
    return hasModuleAccess('beamonscreen.com', requiredRole);
  };

  const isAdminInAnyModule = (): boolean => {
    if (isUserSuperAdmin) return true;
    if (!user?.moduleAndRole) return false;
    return user.moduleAndRole.some((mr: any) => mr.role === 'admin');
  };

  const canManageUsersAndCompanies = (): boolean => {
    return isUserSuperAdmin;
  };

  const canEditSettings = (): boolean => {
    // SUPER-ADMIN can edit everything
    if (isUserSuperAdmin) return true;
    return hasInstantUpdateAccess('user');
  };

  const canManageDatasets = (): boolean => {
    // SUPER-ADMIN can manage everything
    if (isUserSuperAdmin) return true;
    // Only 'admin' role in the module can manage datasets
    return hasInstantUpdateAccess('admin');
  };

  const canManageTemplates = (): boolean => {
    // SUPER-ADMIN can manage everything
    if (isUserSuperAdmin) return true;
    // Both 'user' and 'admin' roles in the module can manage templates
    return hasInstantUpdateAccess('user') || hasInstantUpdateAccess('admin');
  };

  const canManageCampaigns = (): boolean => {
    // SUPER-ADMIN can manage everything
    if (isUserSuperAdmin) return true;
    // Both 'user' and 'admin' roles in the module can manage campaigns
    return hasInstantUpdateAccess('user') || hasInstantUpdateAccess('admin');
  };

  const canViewOnly = (): boolean => {
    return hasInstantUpdateAccess('admin');
  };

  const getModuleRole = (module: string): ModuleRole | null => {
    if (!user?.moduleAndRole) return null;
    
    const moduleAccess = user.moduleAndRole.find((mr: any) => mr.module === module);
    return moduleAccess ? moduleAccess.role as ModuleRole : null;
  };

  const getInstantUpdateRole = (): ModuleRole | null => {
    return getModuleRole('instantupdate.ai');
  };

  return {
    hasModuleAccess,
    hasInstantUpdateAccess,
    hasAutomateTestAccess,
    hasAssetManagementAccess,
    canUseAssetManagement,
    hasInfralytiqsAccess,
    hasAnamnesisCloudAccess,
    hasBeamonScreenAccess,
    isAdminInAnyModule,
    canManageUsersAndCompanies,
    canEditSettings,
    canManageDatasets,
    canManageTemplates,
    canManageCampaigns,
    canViewOnly,
    getModuleRole,
    getInstantUpdateRole,
    isUserSuperAdmin,
    user
  };
}; 